<template>
  <v-row class="pa-8 text-body-2">
    <v-col
      v-if="
        canadaInsuranceStateList?.length ||
        usInsuranceStateList?.length ||
        canadaTermList?.length ||
        usTermList?.length ||
        anyImportantDisclosures
      "
    >
      <h4 class="mb-3">{{ $t(Translations.myServices) }}*</h4>
      <div v-if="canadaInsuranceStateList?.length" class="mb-3">
        <a
          :href="!isPreview ? window.appConfig.crmArticles.primericaInvestmentsUrl : undefined"
          target="_blank"
          >{{ $t(Translations.investmentsAndSavings) }} (Canada)</a
        ><br /><span>{{ canadaInsuranceStateList }}</span>
      </div>
      <div v-if="usInsuranceStateList?.length" class="mb-3">
        <a
          :href="!isPreview ? window.appConfig.crmArticles.primericaInvestmentsUrl : undefined"
          target="_blank"
          >{{ $t(Translations.investmentsAndSavings) }} (US)</a
        ><br /><span>{{ usInsuranceStateList }}</span>
      </div>
      <div v-if="canadaTermList?.length" class="mb-3">
        <a
          :href="
            !isPreview ? window.appConfig.crmArticles.primericaTermLifeInsuranceUrl : undefined
          "
          target="_blank"
        >
          {{ $t(Translations.termLifeInsurance) }} (Canada) </a
        ><br /><span>{{ canadaTermList }}</span>
      </div>
      <div v-if="usTermList?.length" class="mb-3">
        <a
          :href="
            !isPreview ? window.appConfig.crmArticles.primericaTermLifeInsuranceUrl : undefined
          "
          target="_blank"
        >
          {{ $t(Translations.termLifeInsurance) }} (US) </a
        ><br /><span>{{ usTermList }}</span>
      </div>
      <div v-if="anyImportantDisclosures">
        {{ $t(Translations.seeImportantDisclosures) }}
      </div>
      <div>
        <div v-if="canadaAutoHomeOwnerListExists">
          <a
            :href="!isPreview ? window.appConfig.crmArticles.primericaAutoHomeAdUrl : undefined"
            target="_blank"
            >{{ $t(Translations.autoHomeOwners) }} (Canada)</a
          >
        </div>
        <div v-if="usTermList?.length && agentAddress?.state !== 'PR'">
          <a
            :href="!isPreview ? window.appConfig.crmArticles.primericaAutoHomeAdUrl : undefined"
            target="_blank"
            >{{ $t(Translations.autoHomeOwners) }} (US)</a
          >
        </div>
        <div>
          <a
            :href="
              !isPreview
                ? window.appConfig.crmArticles.primericaBusinessOpportunitiesUrl
                : undefined
            "
            target="_blank"
            >{{ $t(Translations.primericaBusinessOpportunity) }}</a
          >
        </div>
      </div>
      <div>
        <div v-if="caLifeInsuranceExists?.length">
          CA {{ $t(Translations.lifeLicense) }}: #{{ caLifeInsuranceExists }}
        </div>
        <div>*{{ $t(Translations.notAvailableInAll) }}</div>
      </div>
    </v-col>
    <v-col
      v-if="
        canadaInsuranceStateList?.length ||
        usInsuranceStateList?.length ||
        canadaTermList?.length ||
        usTermList?.length
      "
    >
      <h4 class="mb-3">{{ $t(Translations.aboutMe) }}</h4>
      <div class="mb-3" v-if="canadaInsuranceStateList?.length">
        {{ $t(Translations.pfsInvestmentsRepCN) }}
      </div>
      <div class="mb-3" v-if="usInsuranceStateList?.length">
        {{ $t(Translations.pfsInvestmentsRepUS) }}:

        <div v-if="!!agentBrokerLink">
          <span>{{ agentName }}</span>
          <a
            :href="
              !isPreview
                ? `${window.appConfig.crmArticles.primericaCardNumberUrl}${agentBrokerLink}`
                : undefined
            "
            target="_blank"
          >
            FINRA's BrokerCheck</a
          >
        </div>
      </div>
      <div class="mb-3" v-if="canadaTermList?.length">
        {{ $t(Translations.lifeInsuranceRep) }} (Canada)
      </div>
      <div class="mb-3" v-if="usTermList?.length">{{ $t(Translations.lifeInsuranceRep) }} (US)</div>
      <div class="mb-3" v-if="canadaInsuranceStateList?.length">
        {{ $t(Translations.investmentAdviserRepCN) }} (Canada)
      </div>
      <div class="mb-3" v-if="usInsuranceStateList?.length">
        {{ $t(Translations.investmentAdviserRepUS) }}
      </div>
    </v-col>
  </v-row>
</template>
<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import type { AgentAddress, CRMAgentDetails, Licenses } from '@/interfaces/PegaAgent';
import { usaStates, canadaProvinces, usaStateAbbreviation } from '@/constants/stateMapping';
import { Translations } from '@/i18n/models';
import { Country } from '@/constants/Countries';
import { LicenseType } from '@/constants/LicenseTypes';

export default defineComponent({
  data() {
    return {
      Translations,
      window,
    };
  },
  props: {
    agentDataRetreived: {
      type: Object as PropType<CRMAgentDetails | null>,
      required: true,
    },
    isPreview: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    services(): Licenses[] {
      return this.agentDataRetreived?.licenses ?? [];
    },
    agentAddress(): AgentAddress | undefined {
      return this.agentDataRetreived?.agentInfo.address;
    },
    agentName(): string {
      return this.agentDataRetreived?.agentInfo.agentname ?? '';
    },
    agentBrokerLink(): string | null {
      const crdNumber = this.agentDataRetreived?.agentInfo.crdnumber;
      if (!crdNumber) return null;
      return crdNumber;
    },
    usInsuranceStateList(): string {
      if (!this.services.length) return '';
      return this.services
        .filter((el: Licenses) => el.product === LicenseType.SEC && el.country === Country.USA)
        .map((el: Licenses) => usaStates[el.state])
        .join(', ');
    },
    canadaInsuranceStateList(): string {
      if (!this.services.length) return '';
      return this.services
        .filter((el: Licenses) => el.product === LicenseType.SEC && el.country === Country.CAN)
        .map((el: Licenses) => canadaProvinces[el.state])
        .join(', ');
    },
    usTermList(): string {
      if (!this.services.length) return '';
      return this.services
        .filter((el: Licenses) => el.product === LicenseType.LIF && el.country === Country.USA)
        .map((el: Licenses) => usaStates[el.state])
        .join(', ');
    },
    canadaTermList(): string {
      if (!this.services.length) return '';
      return this.services
        .filter((el: Licenses) => el.product === LicenseType.LIF && el.country === Country.CAN)
        .map((el: Licenses) => canadaProvinces[el.state])
        .join(', ');
    },
    canadaAutoHomeOwnerListExists(): boolean {
      return !!this.services.find(
        (el: Licenses) => el.product === LicenseType.AH && el.country === Country.CAN
      );
    },
    caLifeInsuranceExists(): string | undefined {
      if (!this.services.length) return;
      return this.services
        .filter((el: Licenses) => el.product === LicenseType.LIF && el.country === Country.USA)
        .find((el: Licenses) => el.state === usaStateAbbreviation.CA)?.licenseNumber;
    },

    anyImportantDisclosures(): boolean {
      return !!(
        this.canadaAutoHomeOwnerListExists ||
        (this.usTermList.length && this.agentAddress?.state !== usaStateAbbreviation.PR) ||
        this.caLifeInsuranceExists
      );
    },
  },
});
</script>
